// Modal de configuração de cotas do processo (precedência e remanejamento - formato planilha)
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import { Alert } from '../ui/Alert';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { styles } from '../../styles/globalStyles';

/** Converte regras para matriz preservando a ordem cadastrada (primeira aparição de cada origem nas regras). */
const rulesToMatrix = (rules, processQuotas) => {
  const bySource = {};
  const rowOrder = [];
  for (const r of rules) {
    const src = r.source_quota_id;
    if (!bySource[src]) {
      bySource[src] = [];
      rowOrder.push(src);
    }
    bySource[src].push({ target: r.target_quota_id, order: parseInt(r.order, 10) || 1 });
  }
  for (const qid of processQuotas) {
    if (!bySource[qid]) {
      rowOrder.push(qid);
      bySource[qid] = [];
    }
  }
  let maxCols = 3;
  for (const src of rowOrder) {
    bySource[src].sort((a, b) => a.order - b.order);
    maxCols = Math.max(maxCols, bySource[src].length);
  }
  const rows = rowOrder.map(qid => {
    const targets = (bySource[qid] || []).map(x => x.target);
    while (targets.length < maxCols) targets.push(null);
    return { source_quota_id: qid, targets };
  });
  return { rows, maxCols };
};

const matrixToRules = (rows) => {
  const rules = [];
  for (const row of rows) {
    row.targets.forEach((tgt, idx) => {
      if (tgt != null) rules.push({ source_quota_id: row.source_quota_id, target_quota_id: tgt, order: idx + 1 });
    });
  }
  return rules;
};

export const ProcessQuotaConfigModal = ({ isOpen, onClose, process, processes = [], onSaved, onCopySuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('quotas');
  const [allQuotas, setAllQuotas] = useState([]);
  const [allTiebreakerCriteria, setAllTiebreakerCriteria] = useState([]);
  const [tiebreakerRules, setTiebreakerRules] = useState([]);
  const [processQuotas, setProcessQuotas] = useState([]);
  const [precedenceRules, setPrecedenceRules] = useState([]);
  const [reallocationRules, setReallocationRules] = useState([]);
  const [precedenceMatrix, setPrecedenceMatrix] = useState({ rows: [], maxCols: 3 });
  const [reallocationMatrix, setReallocationMatrix] = useState({ rows: [], maxCols: 3 });
  const [copySourcePrecedence, setCopySourcePrecedence] = useState('');
  const [copySourceReallocation, setCopySourceReallocation] = useState('');

  const loadData = useCallback(async () => {
    if (!process?.id) return;
    setLoading(true);
    setError('');
    try {
      const [quotas, pq, prec, reall, criteria] = await Promise.all([
        apiClient.getQuotas(true),
        apiClient.getProcessQuotas(process.id),
        apiClient.getProcessPrecedence(process.id),
        apiClient.getProcessReallocation(process.id),
        apiClient.getTiebreakerCriteria(true)
      ]);
      setAllQuotas(Array.isArray(quotas) ? quotas : []);
      const pqIds = Array.isArray(pq) ? pq.map(p => p.quota_id) : [];
      setProcessQuotas(pqIds);
      setPrecedenceRules(Array.isArray(prec) ? prec : []);
      setReallocationRules(Array.isArray(reall) ? reall : []);
      setPrecedenceMatrix(rulesToMatrix(prec || [], pqIds));
      setReallocationMatrix(rulesToMatrix(reall || [], pqIds));
      setAllTiebreakerCriteria(Array.isArray(criteria) ? criteria : []);
      const rules = process.tiebreaker_rules || [];
      setTiebreakerRules(Array.isArray(rules) ? rules : []);
    } catch (err) {
      setError(err.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [process?.id, process?.tiebreaker_rules]);

  useEffect(() => {
    if (isOpen && process?.id) {
      loadData();
      if (!process?.use_quotas) setTab('tiebreaker');
    }
  }, [isOpen, process?.id, process?.use_quotas, loadData]);

  useEffect(() => {
    if (processQuotas.length && tab === 'precedence') {
      setPrecedenceMatrix(prev => prev.rows.length ? prev : rulesToMatrix(precedenceRules, processQuotas));
    }
  }, [processQuotas, tab, precedenceRules]);

  useEffect(() => {
    if (processQuotas.length && tab === 'reallocation') {
      setReallocationMatrix(prev => prev.rows.length ? prev : rulesToMatrix(reallocationRules, processQuotas));
    }
  }, [processQuotas, tab, reallocationRules]);

  const handleQuotaToggle = (quotaId) => {
    setProcessQuotas(prev =>
      prev.includes(quotaId) ? prev.filter(id => id !== quotaId) : [...prev, quotaId]
    );
  };

  const handleSaveQuotas = async () => {
    try {
      setLoading(true);
      await apiClient.saveProcessQuotas(process.id, processQuotas);
      onSaved?.();
      loadData();
    } catch (err) {
      setError(err.message || 'Erro ao salvar cotas');
    } finally {
      setLoading(false);
    }
  };

  const updateMatrixCell = (type, rowIdx, colIdx, value) => {
    const setter = type === 'precedence' ? setPrecedenceMatrix : setReallocationMatrix;
    setter(prev => {
      const rows = prev.rows.map((r, i) => {
        if (i !== rowIdx) return r;
        const targets = [...(r.targets || [])];
        while (targets.length <= colIdx) targets.push(null);
        targets[colIdx] = value === '' || value == null ? null : parseInt(value, 10);
        return { ...r, targets };
      });
      return { ...prev, rows };
    });
  };

  const addColumn = (type) => {
    const setter = type === 'precedence' ? setPrecedenceMatrix : setReallocationMatrix;
    setter(prev => ({
      ...prev,
      maxCols: prev.maxCols + 1,
      rows: prev.rows.map(r => ({ ...r, targets: [...r.targets, null] }))
    }));
  };

  const removeColumn = (type) => {
    const setter = type === 'precedence' ? setPrecedenceMatrix : setReallocationMatrix;
    setter(prev => {
      if (prev.maxCols <= 1) return prev;
      const lastEmpty = prev.rows.every(r => !r.targets[prev.maxCols - 1]);
      if (!lastEmpty) return prev;
      return {
        ...prev,
        maxCols: prev.maxCols - 1,
        rows: prev.rows.map(r => ({ ...r, targets: r.targets.slice(0, -1) }))
      };
    });
  };

  const moveRow = (type, fromIdx, direction) => {
    const setter = type === 'precedence' ? setPrecedenceMatrix : setReallocationMatrix;
    const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
    if (toIdx < 0 || toIdx >= (type === 'precedence' ? precedenceMatrix : reallocationMatrix).rows.length) return;
    setter(prev => {
      const rows = [...prev.rows];
      [rows[fromIdx], rows[toIdx]] = [rows[toIdx], rows[fromIdx]];
      return { ...prev, rows };
    });
  };

  const handleSavePrecedence = async () => {
    try {
      setLoading(true);
      const rules = matrixToRules(precedenceMatrix.rows);
      await apiClient.saveProcessPrecedence(process.id, rules.map(r => ({ ...r, order: r.order })));
      onSaved?.();
      loadData();
    } catch (err) {
      setError(err.message || 'Erro ao salvar precedência');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPrecedence = useCallback(async () => {
    if (!copySourcePrecedence || !processQuotas.length) return;
    try {
      setLoading(true);
      const rules = await apiClient.getProcessPrecedence(parseInt(copySourcePrecedence, 10));
      const pqSet = new Set(processQuotas);
      const filtered = (rules || []).filter(r =>
        pqSet.has(r.source_quota_id) && pqSet.has(r.target_quota_id)
      );
      setPrecedenceMatrix(rulesToMatrix(filtered, processQuotas));
      setCopySourcePrecedence('');
      onCopySuccess?.();
    } catch (err) {
      setError(err.message || 'Erro ao copiar precedência');
    } finally {
      setLoading(false);
    }
  }, [copySourcePrecedence, processQuotas, onCopySuccess]);

  const handleCopyReallocation = useCallback(async () => {
    if (!copySourceReallocation || !processQuotas.length) return;
    try {
      setLoading(true);
      const rules = await apiClient.getProcessReallocation(parseInt(copySourceReallocation, 10));
      const pqSet = new Set(processQuotas);
      const filtered = (rules || []).filter(r =>
        pqSet.has(r.source_quota_id) && pqSet.has(r.target_quota_id)
      );
      setReallocationMatrix(rulesToMatrix(filtered, processQuotas));
      setCopySourceReallocation('');
      onCopySuccess?.();
    } catch (err) {
      setError(err.message || 'Erro ao copiar remanejamento');
    } finally {
      setLoading(false);
    }
  }, [copySourceReallocation, processQuotas, onCopySuccess]);

  const handleSaveReallocation = async () => {
    try {
      setLoading(true);
      const rules = matrixToRules(reallocationMatrix.rows);
      await apiClient.saveProcessReallocation(process.id, rules.map(r => ({ ...r, order: r.order })));
      onSaved?.();
      loadData();
    } catch (err) {
      setError(err.message || 'Erro ao salvar remanejamento');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTiebreakerCriterion = (criterionId) => {
    if (tiebreakerRules.some(r => r.criterion_id === criterionId)) return;
    setTiebreakerRules(prev => [...prev, { criterion_id: criterionId, order: prev.length + 1 }]);
  };

  const handleRemoveTiebreakerCriterion = (idx) => {
    setTiebreakerRules(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, order: i + 1 })));
  };

  const handleMoveTiebreaker = (idx, direction) => {
    const toIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (toIdx < 0 || toIdx >= tiebreakerRules.length) return;
    setTiebreakerRules(prev => {
      const arr = [...prev];
      [arr[idx], arr[toIdx]] = [arr[toIdx], arr[idx]];
      return arr.map((r, i) => ({ ...r, order: i + 1 }));
    });
  };

  const handleSaveTiebreaker = async () => {
    try {
      setLoading(true);
      const { error: updateError } = await apiClient.updateProcess(process.id, {
        tiebreaker_rules: tiebreakerRules.map((r, i) => ({ criterion_id: r.criterion_id, order: i + 1 }))
      });
      if (updateError) throw new Error(updateError);
      onSaved?.();
      loadData();
    } catch (err) {
      setError(err.message || 'Erro ao salvar critérios de desempate');
    } finally {
      setLoading(false);
    }
  };

  const getQuotaLabel = (id) => allQuotas.find(q => q.id === id)?.code || `#${id}`;
  const getTiebreakerLabel = (c) => {
    if (c.data_type === 'age') {
      const min = c.config?.min_age;
      return min != null ? `Idade (>${min} anos)` : 'Idade (geral)';
    }
    const subj = c.config?.subject_name;
    const mode = c.config?.grade_mode === 'average' ? 'Média' : 'Nota';
    return subj ? `${mode}: ${subj}` : 'Nota';
  };

  const tabStyle = (t) => ({
    padding: '8px 16px',
    border: 'none',
    background: tab === t ? '#3B82F6' : '#E5E7EB',
    color: tab === t ? '#fff' : '#374151',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: tab === t ? 600 : 400
  });

  const stickyCol = (left, right) => ({
    position: 'sticky',
    ...(left != null && { left }),
    ...(right != null && { right }),
    backgroundColor: '#fff',
    zIndex: 1,
    boxShadow: left > 0 ? '2px 0 4px rgba(0,0,0,0.06)' : right === 0 ? '-2px 0 4px rgba(0,0,0,0.06)' : 'none'
  });
  const stickyHeader = (left, right) => ({
    ...styles.tableHeaderCell,
    position: 'sticky',
    top: 0,
    ...(left != null && { left }),
    ...(right != null && { right }),
    backgroundColor: '#F3F4F6',
    zIndex: 2,
    boxShadow: left != null ? '2px 0 4px rgba(0,0,0,0.06)' : right === 0 ? '-2px 0 4px rgba(0,0,0,0.06)' : '0 2px 4px rgba(0,0,0,0.06)'
  });

  const MatrixGrid = ({ matrix, type }) => {
    const rows = matrix.rows?.length ? matrix.rows : processQuotas.map(qid => ({ source_quota_id: qid, targets: [null, null, null] }));
    const colLengths = rows.map(r => (r.targets || []).length);
    const cols = Math.max(matrix.maxCols || 3, colLengths.length ? Math.max(...colLengths) : 3);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{
          overflow: 'auto',
          maxHeight: 'min(65vh, 500px)',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          backgroundColor: '#fff'
        }}>
          <table style={{ ...styles.table, margin: 0, minWidth: 'max-content' }}>
            <thead>
              <tr>
                <th style={{ ...stickyHeader(0), minWidth: '48px', width: '48px' }}>Seq</th>
                <th style={{ ...stickyHeader(48), minWidth: '110px', width: '110px' }}>Cota origem</th>
                {Array.from({ length: cols }, (_, i) => (
                  <th key={i} style={styles.tableHeaderCell}>{i + 1}º</th>
                ))}
                <th style={{ ...stickyHeader(null, 0), minWidth: '90px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={`${row.source_quota_id}-${rowIdx}`}>
                  <td style={{ ...styles.tableCell, ...stickyCol(0), fontWeight: 700, color: '#374151', minWidth: '48px', width: '48px' }}>
                    {rowIdx + 1}
                  </td>
                  <td style={{ ...styles.tableCell, ...stickyCol(48), fontWeight: 600, minWidth: '110px', width: '110px' }}>
                    {getQuotaLabel(row.source_quota_id)}
                  </td>
                  {Array.from({ length: cols }, (_, colIdx) => (
                    <td key={colIdx} style={styles.tableCell}>
                      <select
                        value={(row.targets || [])[colIdx] ?? ''}
                        onChange={(e) => updateMatrixCell(type, rowIdx, colIdx, e.target.value)}
                        style={{ ...styles.select, minWidth: '90px' }}
                      >
                        <option value="">—</option>
                        {processQuotas.filter(q => q !== row.source_quota_id).map(qid => (
                          <option key={qid} value={qid}>{getQuotaLabel(qid)}</option>
                        ))}
                      </select>
                    </td>
                  ))}
                  <td style={{ ...styles.tableCell, ...stickyCol(null, 0) }}>
                    <button type="button" onClick={() => moveRow(type, rowIdx, 'up')} style={{ ...styles.button, ...styles.buttonSmall, marginRight: '4px' }} disabled={rowIdx === 0}>↑</button>
                    <button type="button" onClick={() => moveRow(type, rowIdx, 'down')} style={{ ...styles.button, ...styles.buttonSmall }} disabled={rowIdx === rows.length - 1}>↓</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
          Dica: Seq e Cota origem ficam fixos à esquerda, Ações à direita. Role dentro da área acima.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={() => addColumn(type)} style={{ ...styles.button, ...styles.buttonSecondary }}>
            + Adicionar coluna
          </button>
          {cols > 1 && (
            <button type="button" onClick={() => removeColumn(type)} style={{ ...styles.button, ...styles.buttonSecondary }}>
              − Remover última coluna
            </button>
          )}
        </div>
      </div>
    );
  };

  const showQuotaTabs = process?.use_quotas === true;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Configurar: ${process?.name || ''}`} large={tab === 'precedence' || tab === 'reallocation' ? 'extra' : true}>
      {error && <Alert message={error} type="error" onClose={() => setError('')} />}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {showQuotaTabs && (
          <>
            <button style={tabStyle('quotas')} onClick={() => setTab('quotas')}>Cotas do processo</button>
            <button style={tabStyle('precedence')} onClick={() => setTab('precedence')}>Precedência</button>
            <button style={tabStyle('reallocation')} onClick={() => setTab('reallocation')}>Remanejamento</button>
          </>
        )}
        <button style={tabStyle('tiebreaker')} onClick={() => setTab('tiebreaker')}>Desempate</button>
      </div>

      {loading && !processQuotas.length && tab !== 'tiebreaker' ? (
        <LoadingSpinner text="Carregando..." />
      ) : tab === 'quotas' && (
        <div>
          <p style={{ marginBottom: '12px', color: '#6B7280' }}>
            Selecione as cotas que participam deste processo:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {allQuotas.map(q => (
              <label key={q.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 0' }}>
                <input
                  type="checkbox"
                  checked={processQuotas.includes(q.id)}
                  onChange={() => handleQuotaToggle(q.id)}
                />
                <span><strong>{q.code}</strong> – {q.description}</span>
              </label>
            ))}
          </div>
          {allQuotas.length === 0 && <p style={{ color: '#6B7280' }}>Cadastre cotas em Gerenciamento de Cotas.</p>}
          <div style={{ marginTop: '16px' }}>
            <button style={{ ...styles.button, ...styles.buttonPrimary }} onClick={handleSaveQuotas} disabled={loading}>
              Salvar cotas
            </button>
          </div>
        </div>
      )}

      {tab === 'precedence' && (
        <div>
          <p style={{ marginBottom: '12px', color: '#6B7280' }}>
            Precedência: Seq = ordem cadastrada (define desempate). Use ↑↓ para alterar a sequência. Colunas = 1º, 2º, 3º destino...
          </p>
          {processQuotas.length >= 2 && processes.length > 1 && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F0F9FF', borderRadius: '8px', border: '1px solid #BAE6FD' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px' }}>Copiar modelo de outro processo:</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={copySourcePrecedence}
                  onChange={(e) => setCopySourcePrecedence(e.target.value)}
                  style={{ ...styles.select, minWidth: '200px' }}
                >
                  <option value="">Selecione o processo...</option>
                  {processes.filter(p => p.id !== process?.id).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleCopyPrecedence}
                  disabled={!copySourcePrecedence || loading}
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                >
                  📋 Copiar Precedência
                </button>
              </div>
              <p style={{ fontSize: '11px', color: '#6B7280', margin: '6px 0 0 0' }}>
                Copia apenas regras cujas cotas existem neste processo.
              </p>
            </div>
          )}
          {processQuotas.length < 2 ? (
            <p style={{ color: '#6B7280' }}>Selecione pelo menos 2 cotas na aba &quot;Cotas do processo&quot;.</p>
          ) : (
            <>
              <MatrixGrid matrix={precedenceMatrix} type="precedence" />
              <div style={{ marginTop: '16px' }}>
                <button style={{ ...styles.button, ...styles.buttonPrimary }} onClick={handleSavePrecedence} disabled={loading}>
                  Salvar precedência
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'reallocation' && (
        <div>
          <p style={{ marginBottom: '12px', color: '#6B7280' }}>
            Remanejamento: Seq = ordem cadastrada. Use ↑↓ para alterar a sequência. Colunas = 1º, 2º, 3º destino...
          </p>
          {processQuotas.length >= 2 && processes.length > 1 && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px' }}>Copiar modelo de outro processo:</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={copySourceReallocation}
                  onChange={(e) => setCopySourceReallocation(e.target.value)}
                  style={{ ...styles.select, minWidth: '200px' }}
                >
                  <option value="">Selecione o processo...</option>
                  {processes.filter(p => p.id !== process?.id).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleCopyReallocation}
                  disabled={!copySourceReallocation || loading}
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                >
                  📋 Copiar Remanejamento
                </button>
              </div>
              <p style={{ fontSize: '11px', color: '#6B7280', margin: '6px 0 0 0' }}>
                Copia apenas regras cujas cotas existem neste processo.
              </p>
            </div>
          )}
          {processQuotas.length < 2 ? (
            <p style={{ color: '#6B7280' }}>Selecione pelo menos 2 cotas na aba &quot;Cotas do processo&quot;.</p>
          ) : (
            <>
              <MatrixGrid matrix={reallocationMatrix} type="reallocation" />
              <div style={{ marginTop: '16px' }}>
                <button style={{ ...styles.button, ...styles.buttonPrimary }} onClick={handleSaveReallocation} disabled={loading}>
                  Salvar remanejamento
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'tiebreaker' && (
        <div>
          <p style={{ marginBottom: '12px', color: '#6B7280' }}>
            Em caso de empate na nota final, o desempate será aplicado na ordem abaixo. Cadastre critérios em Configuração → Critérios de desempate.
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '280px' }}>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#374151' }}>Ordem de desempate</h4>
              {tiebreakerRules.length === 0 ? (
                <p style={{ color: '#6B7280', fontSize: '13px' }}>Nenhum critério selecionado. Adicione à direita.</p>
              ) : (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                  {tiebreakerRules.map((r, idx) => {
                    const c = allTiebreakerCriteria.find(cr => cr.id === r.criterion_id);
                    return (
                      <div
                        key={`${r.criterion_id}-${idx}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 12px',
                          borderBottom: idx < tiebreakerRules.length - 1 ? '1px solid #E5E7EB' : 'none',
                          backgroundColor: '#fff'
                        }}
                      >
                        <span style={{ fontWeight: 600, marginRight: '12px', minWidth: '24px' }}>{idx + 1}º</span>
                        <span style={{ flex: 1 }}>{c ? getTiebreakerLabel(c) : `Critério #${r.criterion_id}`}</span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button type="button" onClick={() => handleMoveTiebreaker(idx, 'up')} style={{ ...styles.button, ...styles.buttonSmall }} disabled={idx === 0}>↑</button>
                          <button type="button" onClick={() => handleMoveTiebreaker(idx, 'down')} style={{ ...styles.button, ...styles.buttonSmall }} disabled={idx === tiebreakerRules.length - 1}>↓</button>
                          <button type="button" onClick={() => handleRemoveTiebreakerCriterion(idx)} style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall }}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop: '16px' }}>
                <button style={{ ...styles.button, ...styles.buttonPrimary }} onClick={handleSaveTiebreaker} disabled={loading}>
                  Salvar ordem de desempate
                </button>
              </div>
            </div>
            <div style={{ flex: '1', minWidth: '240px' }}>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#374151' }}>Adicionar critério</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {allTiebreakerCriteria
                  .filter(c => !tiebreakerRules.some(r => r.criterion_id === c.id))
                  .map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleAddTiebreakerCriterion(c.id)}
                      style={{ ...styles.button, ...styles.buttonSecondary, textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      + {getTiebreakerLabel(c)}
                    </button>
                  ))}
                {allTiebreakerCriteria.filter(c => !tiebreakerRules.some(r => r.criterion_id === c.id)).length === 0 && (
                  <p style={{ color: '#6B7280', fontSize: '13px' }}>Todos os critérios já foram adicionados.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
        <button style={{ ...styles.button, ...styles.buttonSecondary }} onClick={onClose}>Fechar</button>
      </div>
    </Modal>
  );
};
